/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import type { ColumnsType } from 'antd/es/table';
import { SetStateAction } from 'react';

import { Subtenant } from '@/client';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { SubtenantActionDropdown } from '@/pages/Subtenants/SubtenantActionDropdown';
import { Link } from '@/primitives/Link';
import { Space } from '@/primitives/Space';
import { Text } from '@/primitives/Text';

export function useSubtenantListColumns(
  editSubtenant: (subtenant: Subtenant) => void,
  deleteSubtenant: React.Dispatch<SetStateAction<Subtenant | undefined>>,
): ColumnsType<Subtenant> {
  const { t } = useMimicTranslation('subtenants');
  const canEditSubtenants = useAuthorization('editor');
  const columns: ColumnsType<Subtenant> = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'id',
      render: (_, subtenant) => {
        return (
          <Link to={`/tenants/${subtenant.tenantID}/subtenants/${subtenant.id}`}>
            <Text size="md">{subtenant.name}</Text>
          </Link>
        );
      },
    },
    {
      title: t('parent'),
      dataIndex: 'parent',
      width: 250,
      key: 'id',
      render: (_, subtenant) => {
        return <Text type="default">{subtenant.parent?.name || '-'}</Text>;
      },
    },
  ];

  if (!canEditSubtenants) return columns;

  return [
    ...columns,
    {
      title: '',
      dataIndex: '',
      key: 'actions',
      width: 64,
      render: (_, record) => {
        return (
          <Space compact>
            <SubtenantActionDropdown
              subtenant={record}
              onDeleteSubtenant={() => deleteSubtenant(record)}
              editSubtenant={editSubtenant}
            />
          </Space>
        );
      },
    },
  ];
}
